import { Content, Icon, ListCell, ListView, Typography } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import {
  getClickStreamEvent,
  getEventAnalytic,
  useGetClickStreamCashierContext,
  useGetClickStreamPayGroupCashierContext,
} from 'services/clickstream';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { Notification } from 'store/models';
import { depositMethodsSelector } from 'store/slices/deposit';
import { paymentFlowSelector } from 'store/slices/global';
import { removeNotification } from 'store/slices/notifications';
import { withdrawalMethodsSelector } from 'store/slices/withdrawal';

import { isDepositFlowType, isEmpty } from '../../../utils';
import { BACKGROUND_TYPE, DEFAULT_NOTIFICATION_STYLE, FILL_MAP, ICON_MAP, TEXT_COLOR } from './constants';
import styles from './Notifications.module.scss';

interface NotificationItemProps {
  notification: Notification;
  key?: string;
}

const clickstream = getEventAnalytic();

export const NotificationItem: FC<NotificationItemProps> = (props) => {
  const { notification } = props;
  const { key, style = DEFAULT_NOTIFICATION_STYLE, value, params, extraValue } = notification;
  const dispatch = useAppDispatch();
  const { translate } = useSkeletonTranslation();
  const iconType = ICON_MAP[style];
  const iconFill = FILL_MAP[style];
  const backgroundType = BACKGROUND_TYPE[style];
  const textColor = TEXT_COLOR[style];
  const flowType = useAppSelector(paymentFlowSelector);
  const isDeposit = isDepositFlowType(flowType);
  const depositMethods = useAppSelector(depositMethodsSelector);
  const withdrawalMethods = useAppSelector(withdrawalMethodsSelector);
  const shouldClose = isDeposit ? !!depositMethods?.length : !!withdrawalMethods?.length;

  const cashierContext = useGetClickStreamCashierContext(flowType);
  const payGroupContext = useGetClickStreamPayGroupCashierContext(flowType);
  const contextToPush = !isEmpty(payGroupContext.data) ? [cashierContext, payGroupContext] : [cashierContext];

  const convertedValue = (value ?? '').replace(/:/g, '&#xa789;');
  const convertedExtraValue = (extraValue ?? '').replace(/:/g, '&#xa789;');
  const translatedValue = translate(convertedValue, {
    ...params,
  });
  const translatedExtraValue = translate(convertedExtraValue);

  const message = `${translatedValue} ${translatedExtraValue}`;

  const handleDeleteItem = () => {
    const event = getClickStreamEvent.cashier_method_infoblock_click('close');
    clickstream.push(event, contextToPush);
    dispatch(removeNotification(key as string));
  };

  const icon = <Icon data-testid="notification-item-icon" name={iconType} fill={iconFill} />;

  const closeIcon = <Content icon="close" onClick={handleDeleteItem} className={styles.action} />;

  const content = (
    <div className={styles.contentWrapper}>
      <Typography
        color={textColor}
        variant="caption-1-regular"
        data-testid="notification-item-message"
        dangerouslySetInnerHTML={{
          __html: message || '',
        }}
      />
    </div>
  );

  return (
    <ListView data-testid="notification-view-item">
      <ListCell
        icon={icon}
        leftContent={content}
        rightImage={shouldClose ? closeIcon : ''}
        data-translation-key={value}
        data-testid="notification-listcell-item"
        className={styles.item}
        style={{
          background: backgroundType,
        }}
      />
    </ListView>
  );
};
